<section class="service-category-detail-page">

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <div class="page-header">
    <div class="header-left">
      <button class="ghost-btn back-btn" type="button" (click)="back()">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        Back
      </button>
      @if (category()) {
        <h1>{{ category()!.name }}</h1>
      } @else {
        <h1>Service Category</h1>
      }
    </div>
  </div>

  <!-- ── Loading ────────────────────────────────────────────────────────── -->
  @if (loading()) {
    <div class="page-loading">
      <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
    </div>

  } @else if (error()) {
    <div class="page-error">{{ error() }}</div>

  } @else if (category(); as cat) {

    <!-- ── Body: 75% / 25% ─────────────────────────────────────────────── -->
    <div class="page-body">

      <!-- ════════ LEFT COLUMN ════════ -->
      <div class="left-col">

        <!-- ── Identity ──────────────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">category</mat-icon>
            <h2 class="section-title">Identity</h2>
            <span class="status-chip" [class.status-chip--active]="cat.is_active" [class.status-chip--inactive]="!cat.is_active">
              {{ cat.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="fields-grid">

            <div class="field">
              <span class="field-label">Vertical</span>
              <span class="vertical-chip vertical-chip--{{ cat.vertical }}">
                {{ verticalLabel(cat.vertical) }}
              </span>
            </div>

            <div class="field">
              <span class="field-label">Added</span>
              <span class="field-value field-value--muted">{{ formatDate(cat.created_at) }}</span>
            </div>

            <div class="field field--wide">
              <span class="field-label">Category ID</span>
              <span class="field-value field-value--mono">{{ cat.category_id }}</span>
            </div>

          </div>
        </div>

        <!-- ── LLM Description ──────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">smart_toy</mat-icon>
            <h2 class="section-title">LLM Description</h2>
          </div>

          @if (cat.description_ai) {
            <p class="description-text">{{ cat.description_ai }}</p>
          } @else {
            <p class="description-text description-text--empty">No description configured.</p>
          }
        </div>

        <!-- ── Example Requests ─────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">chat_bubble_outline</mat-icon>
            <h2 class="section-title">Example Requests</h2>
            @if (cat.example_requests?.length) {
              <span class="count-badge">{{ cat.example_requests!.length }}</span>
            }
          </div>

          @if (cat.example_requests?.length) {
            <div class="pills-list">
              @for (req of cat.example_requests!; track req) {
                <span class="example-pill">"{{ req }}"</span>
              }
            </div>
          } @else {
            <p class="placeholder-text">No example requests configured.</p>
          }
        </div>

      </div>
      <!-- end left-col -->

      <!-- ════════ RIGHT COLUMN ════════ -->
      <div class="right-col">

        <!-- ── City Availability ──────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">location_on</mat-icon>
            <h2 class="section-title">City Availability</h2>
          </div>

          <div class="fields-stack">

            @if (cat.city_availability.length === 0) {
              <p class="placeholder-text">No cities configured.</p>
            } @else {

              @if (activeCities().length) {
                <div class="field field--wide">
                  <span class="field-label">Active</span>
                  <div class="city-list">
                    @for (c of activeCities(); track c.city_code) {
                      <div class="city-row">
                        <span class="city-code">{{ c.city_code }}</span>
                        <span class="bool-chip bool-chip--yes">Active</span>
                      </div>
                    }
                  </div>
                </div>
              }

              @if (inactiveCities().length) {
                <div class="field field--wide">
                  <span class="field-label">Inactive</span>
                  <div class="city-list">
                    @for (c of inactiveCities(); track c.city_code) {
                      <div class="city-row">
                        <span class="city-code city-code--inactive">{{ c.city_code }}</span>
                        <span class="bool-chip bool-chip--no">Off</span>
                      </div>
                    }
                  </div>
                </div>
              }

              <div class="field field--wide">
                <span class="field-label">Summary</span>
                <span class="field-value field-value--muted">
                  {{ activeCities().length }} active · {{ inactiveCities().length }} inactive · {{ cat.city_availability.length }} total
                </span>
              </div>

            }
          </div>
        </div>

        <!-- ── Provider Coverage ─────────────────────────────────────────── -->
        <div class="section-card">
          <div class="section-header">
            <mat-icon class="section-icon">engineering</mat-icon>
            <h2 class="section-title">Provider Coverage</h2>
          </div>

          <div class="fields-stack">

            <div class="field field--wide">
              <span class="field-label">Providers linked</span>
              <span class="provider-count-badge" [class.provider-count-badge--zero]="cat.provider_count === 0">
                {{ cat.provider_count }}
              </span>
            </div>

            <button class="ghost-btn ghost-btn--full" type="button" (click)="viewProviders()">
              <mat-icon aria-hidden="true">open_in_new</mat-icon>
              View providers
            </button>

          </div>
        </div>

        <!-- ── Entrypoints (placeholder) ─────────────────────────────────── -->
        <div class="section-card section-card--placeholder">
          <div class="section-header">
            <mat-icon class="section-icon section-icon--muted">input</mat-icon>
            <h2 class="section-title section-title--muted">Service Entrypoints</h2>
          </div>
          <p class="placeholder-text">Entrypoint configuration coming soon — link catalog items and availability rules to this category.</p>
        </div>

      </div>
      <!-- end right-col -->

    </div>
    <!-- end page-body -->

  }

</section>